<div class="container">
  <div class="checkout">
    <div class="topbar">
      <button type="button" class="back" (click)="breadcrumb()">
        <i class="fa-solid fa-arrow-left"></i>
        <span>Back to Menu</span>
      </button>

      <h1 class="page-title">Checkout</h1>
    </div>

    <div class="shell">
      <div class="details-col">
        <div class="shell-head">
          <h2>Details</h2>
        </div>

        <div class="shell-body">
          <!-- ORDER TYPE TOGGLE -->
          <div class="order-toggle">
            <button
              type="button"
              [class.active]="orderType === 'collection'"
              (click)="setOrderType('collection')"
              >
              Collection
            </button>

            <button
              type="button"
              [class.active]="orderType === 'delivery'"
              [disabled]="
                !isDeliveryOpenNow || (pauseActive && pauseClosedDelivery)
              "
              [attr.aria-disabled]="
                !isDeliveryOpenNow || (pauseActive && pauseClosedDelivery)
              "
              (click)="setOrderType('delivery')"
              >
              Delivery
              @if (!isDeliveryOpenNow && todayDeliveryHours?.openTime) {
                <span
                  class="meta"
                  >
                  (Opens {{ formatTimeHHmm(todayDeliveryHours?.openTime) }})
                </span>
              }
            </button>
          </div>

          <div>
            <form
              [formGroup]="checkoutForm"
              (ngSubmit)="onSubmit()"
              (keydown.enter)="onCheckoutEnterKey($event)"
              class="co-form"
              [class.is-loading]="distanceLoading"
              [attr.aria-busy]="distanceLoading"
              >
              @if (distanceLoading) {
                <div
                  class="loading-overlay"
                  role="status"
                  aria-live="polite"
                  >
                  <i class="fa-solid fa-spinner fa-spin" aria-hidden="true"></i>
                  <span class="loading-text">Calculating delivery distance…</span>
                </div>
              }

              <fieldset [disabled]="distanceLoading">
                <section class="details">
                  <h3><strong>Your Details</strong></h3>

                  @if (currentUser) {
                    <p class="signin-hint">
                      <i class="fa-solid fa-circle-info"></i>
                      These details come from your account and can’t be edited
                      here. Click below if you need to provide a different
                      address.
                    </p>
                  }

                  <div class="addr-grid">
                    <div class="field">
                      <label for="fName">First Name</label>
                      <input
                        id="fName"
                        formControlName="fName"
                        type="text"
                        autocomplete="given-name"
                        [class.is-invalid]="isFieldInvalid('fName')"
                        [readonly]="isIdentityReadOnly"
                        [class.readonly]="isIdentityReadOnly"
                        [attr.aria-readonly]="isIdentityReadOnly"
                        [attr.tabindex]="isIdentityReadOnly ? -1 : null"
                        />
                      @if (isFieldInvalid('fName')) {
                        <div class="error">
                          {{ getFieldError("fName") }}
                        </div>
                      }
                    </div>

                    <div class="field">
                      <label for="lName">Last Name</label>
                      <input
                        id="lName"
                        formControlName="lName"
                        type="text"
                        autocomplete="family-name"
                        [class.is-invalid]="isFieldInvalid('lName')"
                        [readonly]="isIdentityReadOnly"
                        [class.readonly]="isIdentityReadOnly"
                        [attr.aria-readonly]="isIdentityReadOnly"
                        [attr.tabindex]="isIdentityReadOnly ? -1 : null"
                        />
                      @if (isFieldInvalid('lName')) {
                        <div class="error">
                          {{ getFieldError("lName") }}
                        </div>
                      }
                    </div>

                    <div class="field">
                      <label for="email">Email</label>
                      <input
                        id="email"
                        formControlName="email"
                        type="email"
                        inputmode="email"
                        autocomplete="email"
                        [class.is-invalid]="isFieldInvalid('email')"
                        [readonly]="isIdentityReadOnly"
                        [class.readonly]="isIdentityReadOnly"
                        [attr.aria-readonly]="isIdentityReadOnly"
                        [attr.tabindex]="isIdentityReadOnly ? -1 : null"
                        />
                      @if (isFieldInvalid('email')) {
                        <div class="error">
                          {{ getFieldError("email") }}
                        </div>
                      }
                    </div>

                    <div class="field">
                      <label for="phone">Phone</label>
                      <input
                        id="phone"
                        formControlName="phone"
                        type="tel"
                        inputmode="numeric"
                        autocomplete="tel"
                        maxlength="15"
                        (input)="onPhoneInput($event)"
                        [class.is-invalid]="isFieldInvalid('phone')"
                        [readonly]="isIdentityReadOnly"
                        [class.readonly]="isIdentityReadOnly"
                        [attr.aria-readonly]="isIdentityReadOnly"
                        [attr.tabindex]="isIdentityReadOnly ? -1 : null"
                        />
                      @if (isFieldInvalid('phone')) {
                        <div class="error">
                          {{ getFieldError("phone") }}
                        </div>
                      }
                    </div>

                    @if (isDelivery) {
                      @if (currentUser) {
                        <div
                          class="field field-full"
                          >
                          <label for="savedDeliveryAddress">Delivering to</label>
                          <select
                            id="savedDeliveryAddress"
                            [ngModel]="selectedDeliveryAddressId"
                          (ngModelChange)="
                            onDeliveryAddressSelectionChange($event)
                          "
                            [ngModelOptions]="{ standalone: true }"
                            >
                            @for (address of savedAddresses; track address) {
                              <option
                                [ngValue]="address.id"
                                >
                                {{ formatSavedAddress(address) }}
                              </option>
                            }
                            <option [ngValue]="'new'">+ Add a new address</option>
                          </select>
                        </div>
                      }
                      @if (!currentUser || isAddingNewAddress) {
                        <div
                          class="field field-full"
                          >
                          <label for="addressLine1">Address Line 1</label>
                          <div
                            id="addressLine1"
                            #placeAutocompleteHost
                            class="google-place-autocomplete"
                            [class.is-invalid]="isFieldInvalid('addressLine1')"
                          ></div>
                          @if (
                            !isUsingSavedDeliveryAddress &&
                            isFieldInvalid('addressLine1')
                            ) {
                            <div
                              class="error"
                              >
                              {{ getFieldError("addressLine1") }}
                            </div>
                          }
                          @if (
                            !isUsingSavedDeliveryAddress &&
                            !hasResolvedDeliveryAddress
                            ) {
                            <small
                              >
                              Start typing and pick your address from the list
                            </small>
                          }
                          @if (
                            !isUsingSavedDeliveryAddress &&
                            hasResolvedDeliveryAddress
                            ) {
                            <small
                              >
                              Address found. You can add extra delivery details
                              below if needed.
                            </small>
                          }
                        </div>
                      }
                      @if (!currentUser || isAddingNewAddress) {
                        @if (canShowResolvedAddressFields) {
                          <div class="field field-full">
                            <label for="addressLine2">Address Line 2</label>
                            <input
                              id="addressLine2"
                              formControlName="addressLine2"
                              type="text"
                              autocomplete="address-line2"
                              />
                            <small
                              >Add flat, unit, building or other extra details
                              if needed</small
                              >
                            </div>
                            <div class="resolved-grid">
                              <div class="field">
                                <label for="city">City</label>
                                <input
                                  id="city"
                                  formControlName="city"
                                  type="text"
                                  readonly
                                  class="readonly"
                                  aria-readonly="true"
                                  tabindex="-1"
                                  />
                              </div>
                              <div class="field">
                                <label for="county">County</label>
                                <input
                                  id="county"
                                  formControlName="county"
                                  type="text"
                                  readonly
                                  class="readonly"
                                  aria-readonly="true"
                                  tabindex="-1"
                                  />
                              </div>
                              <div class="field">
                                <label for="postalCode">Postal Code</label>
                                <input
                                  id="postalCode"
                                  formControlName="postalCode"
                                  type="text"
                                  readonly
                                  class="readonly"
                                  aria-readonly="true"
                                  tabindex="-1"
                                  />
                              </div>
                            </div>
                          }
                        }
                      }
                    </div>

                    @if (currentUser && isDelivery && hasSavedAddresses) {
                      <button
                        type="button"
                        class="change-address btn-outline"
                    (click)="
                      selectedDeliveryAddressId = 'new';
                      onDeliveryAddressSelectionChange('new')
                    "
                        >
                        Change Address
                      </button>
                    }
                  </section>
                </fieldset>

                @if (
                  isDelivery &&
                  !distanceLoading &&
                  distanceMiles == null &&
                  !loadingSavedAddresses &&
                  !distanceCheckError
                  ) {
                  <div
                    class="range-note"
                    >
                    {{
                    isUsingSavedDeliveryAddress
                    ? "Checking your saved address is within delivery range..."
                    : "Please select your address from the Google suggestions so we can confirm delivery range."
                    }}
                  </div>
                }

                @if (distanceCheckError) {
                  <div class="error-banner">
                    {{ distanceCheckError }}
                  </div>
                }

                @if (isBelowMinimum) {
                  <p class="min-warning">
                    Add {{ amountRemainingToMinimum | currency: "GBP" }} more to
                    reach the minimum delivery amount. Small-order fees count
                    towards this minimum, but delivery and service fees do not.
                  </p>
                }

                @if (addressSaveError) {
                  <div class="error-banner">
                    {{ addressSaveError }}
                  </div>
                }

                @if (submitError) {
                  <div class="error-banner">
                    {{ submitError }}
                  </div>
                }

                <button
                  class="pay btn-primary"
                  type="submit"
                  (click)="submitTrigger = 'button'"
                [disabled]="
                  checkoutForm.invalid ||
                  distanceLoading ||
                  orderSubmitting ||
                  tosLoading ||
                  isOutOfDeliveryArea ||
                  needsDistanceCheck ||
                  isBelowMinimum ||
                  !hasUsableDeliveryAddress ||
                  !!distanceCheckError
                "
                  >
                  {{
                  orderSubmitting
                  ? "Processing..."
                  : tosLoading
                  ? "Loading terms..."
                  : "Proceed to Payment"
                  }}
                </button>

                <p class="terms-copy">
                  By continuing, you accept our
                  <a
                    routerLink="/terms-of-service"
                    target="_blank"
                    rel="noopener"
                    >
                    Terms of Service</a
                    >.
                  </p>
                </form>
              </div>
            </div>
          </div>
          <!-- Sidebar: Basket -->
          <aside class="side">
            @if (!currentUser) {
              <div
                class="auth-box"
                aria-label="Account sign in prompt"
                >
                <div class="auth-head">
                  <h2>Have an account?</h2>
                </div>
                <div class="auth-copy">
                  <p>
                    Log in for faster checkout, or create an account to save your
                    details for next time.
                  </p>
                </div>
                <div class="auth-actions">
                  <button type="button" class="btn-outline" (click)="goToLogin()">
                    Log in
                  </button>
                  <button type="button" class="btn-primary" (click)="goToRegister()">
                    Create Account
                  </button>
                </div>
              </div>
            }
            <app-order-summary
              [title]="'Basket'"
              [cartItems]="cartItems"
              [orderNotes]="orderNotes"
              [isEditingNotes]="isEditingNotes"
              [orderNotesDraft]="orderNotesDraft"
              [serviceFee]="serviceFee"
              [deliveryFee]="deliveryFeeOriginalGross"
              [smallOrderFee]="checkoutSmallOrderFee"
              [total]="checkoutTotal"
              [isDelivery]="isDelivery"
              [editableNotes]="true"
              (startEditNotes)="startEditNotes()"
              (cancelEditNotes)="cancelEditNotes()"
              (saveNotes)="saveNotesFromCheckout()"
              (orderNotesDraftChange)="orderNotesDraft = $event"
            ></app-order-summary>
          </aside>

          <!-- Out-of-area modal -->
          <axis-modal
            [show]="showTooFarModal"
            (closed)="onTooFarModalClosed()"
            [closeOnBackdrop]="true"
            [disableEscape]="false"
            >
            <div class="auth-modal">
              <h3>We’re a bit too far 😕</h3>
              <p>
                You’re about
                <strong>{{ distanceMiles | number: "1.1-1" }}</strong>
                miles away. We currently deliver within
                <strong>{{ MAX_DELIVERY_MILES }}</strong> miles.
              </p>

              <div class="btn-row">
                <button
                  type="button"
                  class="btn-primary"
                  (click)="acceptTooFarSwitchToCollection()"
                  >
                  Switch to collection
                </button>
                <button
                  type="button"
                  class="btn-outline"
                  (click)="keepDeliveryEditAddress()"
                  >
                  Keep delivery & edit address
                </button>
              </div>
            </div>
          </axis-modal>

          <axis-modal
            [show]="showPauseOrdersModal"
            (closed)="onClosePauseOrdersModal()"
            [closeOnBackdrop]="true"
            [disableEscape]="false"
            style="--modal-width: 520px"
            >
            <div modal-header>
              <h2>Orders Temporarily Paused</h2>
              <button class="close-btn" (click)="onClosePauseOrdersModal()">
                ✖
              </button>
            </div>

            <div class="modal-content">
              @if (pauseUntilLabel) {
                <p>
                  We’re not taking
                  <strong>
                    {{
                    pauseModalType === "both"
                    ? "Delivery or Collection"
                    : (pauseModalType || orderType | titlecase)
                    }}
                  </strong>
                  orders right now. Please check back after
                  <strong>{{ pauseUntilLabel }}</strong
                    >.
                  </p>
                } @else {
                  <p>
                    We’re not taking
                    <strong>
                      {{
                      pauseModalType === "both"
                      ? "Delivery or Collection"
                      : (pauseModalType || orderType | titlecase)
                      }}
                    </strong>
                    orders right now. Please check back soon.
                  </p>
                }


                @if (isAllOrderingPaused) {
                  <p style="opacity: 0.85">
                    Collection and delivery are currently paused.
                  </p>
                }

                <div
            style="
              margin-top: 1rem;
              display: flex;
              gap: 0.5rem;
              justify-content: flex-end;
            "
                  >
                  @if (
                    orderType === 'delivery' &&
                    pauseActive &&
                    pauseClosedDelivery &&
                    !pauseClosedCollection
                    ) {
                    <button
                      class="btn-primary"
                      (click)="setOrderType('collection')"
                      >
                      Switch to collection
                    </button>
                  }

                  <button class="btn-primary" (click)="onClosePauseOrdersModal()">
                    OK
                  </button>
                </div>
              </div>
            </axis-modal>
          </div>
        </div>
      </div>
